import React, { useState, useRef, useEffect } from "react";
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  differenceInDays,
  startOfDay,
  endOfDay,
  addMonths,
  subMonths,
  startOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  isBefore,
  isAfter,
} from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import Switch from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Label } from "@/components/Label";
import { dashboardColors, dashboardShadows } from "@/lib/theme";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
  /**
   * Compare-with-previous-period toggle. Optional — when both `compareMode`
   * and `onCompareModeChange` are omitted the switch (and comparison hint)
   * are hidden, so the picker can be reused outside the dashboard.
   */
  compareMode?: boolean;
  onCompareModeChange?: (enabled: boolean) => void;
  className?: string;
}

type PresetRange = {
  label: string;
  getValue: () => { start: Date; end: Date };
};

const presetRanges: PresetRange[] = [
  {
    label: "Aujourd'hui",
    getValue: () => ({
      start: startOfDay(new Date()),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: "Hier",
    getValue: () => ({
      start: startOfDay(subDays(new Date(), 1)),
      end: endOfDay(subDays(new Date(), 1)),
    }),
  },
  {
    label: "7 Derniers Jours",
    getValue: () => ({
      start: startOfDay(subDays(new Date(), 6)),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: "30 Derniers Jours",
    getValue: () => ({
      start: startOfDay(subDays(new Date(), 29)),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: "365 Derniers Jours",
    getValue: () => ({
      start: startOfDay(subDays(new Date(), 364)),
      end: endOfDay(new Date()),
    }),
  },
  {
    label: "Ce Mois",
    getValue: () => ({
      start: startOfMonth(new Date()),
      end: endOfMonth(new Date()),
    }),
  },
  {
    label: "Mois Dernier",
    getValue: () => {
      const lastMonth = subMonths(new Date(), 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth),
      };
    },
  },
  {
    label: "Cette Année",
    getValue: () => ({
      start: startOfYear(new Date()),
      end: endOfYear(new Date()),
    }),
  },
  {
    label: "Année Dernière",
    getValue: () => {
      const lastYear = new Date();
      lastYear.setFullYear(lastYear.getFullYear() - 1);
      return {
        start: startOfYear(lastYear),
        end: endOfYear(lastYear),
      };
    },
  },
];

const capitalizeMonth = (dateStr: string) => {
  return dateStr.replace(/\b[a-z]/g, (char) => char.toUpperCase());
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  compareMode,
  onCompareModeChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [leftMonth, setLeftMonth] = useState(new Date());
  const [rightMonth, setRightMonth] = useState(addMonths(new Date(), 1));
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (startDate) {
      const start = new Date(startDate);
      setLeftMonth(start);
      setRightMonth(addMonths(start, 1));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCurrentPresetLabel = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    for (const preset of presetRanges) {
      const range = preset.getValue();
      if (isSameDay(start, range.start) && isSameDay(end, range.end)) {
        return preset.label;
      }
    }

    return "Période personnalisée";
  };

  const handlePresetClick = (preset: PresetRange) => {
    const range = preset.getValue();
    onDateChange(
      format(range.start, "yyyy-MM-dd"),
      format(range.end, "yyyy-MM-dd")
    );
    setIsOpen(false);
  };

  const handleDateClick = (date: Date) => {
    if (!tempStartDate || tempEndDate) {
      setTempStartDate(date);
      setTempEndDate(null);
      return;
    }

    if (isBefore(date, tempStartDate)) {
      setTempEndDate(tempStartDate);
      setTempStartDate(date);
    } else {
      setTempEndDate(date);
    }
  };

  const applyDateRange = () => {
    if (tempStartDate && tempEndDate) {
      onDateChange(
        format(tempStartDate, "yyyy-MM-dd"),
        format(tempEndDate, "yyyy-MM-dd")
      );
      setIsOpen(false);
    }
  };

  const handlePrevious = () => {
    setLeftMonth(subMonths(leftMonth, 1));
    setRightMonth(subMonths(rightMonth, 1));
  };

  const handleNext = () => {
    setLeftMonth(addMonths(leftMonth, 1));
    setRightMonth(addMonths(rightMonth, 1));
  };

  const isRightCalendarCurrentMonth = isSameMonth(rightMonth, new Date());

  const renderCalendarMonth = (currentMonth: Date) => {
    const monthStart = startOfMonth(currentMonth);
    const startDateOfMonth = startOfWeek(monthStart, { weekStartsOn: 1 });

    const dateRange: Date[] = [];
    let currentDate = startDateOfMonth;

    // Always show 6 weeks (42 days) for consistent calendar height
    for (let i = 0; i < 42; i++) {
      dateRange.push(currentDate);
      currentDate = addDays(currentDate, 1);
    }

    const weekDays = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

    return (
      <div className="p-3">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-xs font-medium text-gray-500 text-center py-1"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {dateRange.map((date, index) => {
            const isCurrentMonth = isSameMonth(date, currentMonth);
            const isToday = isSameDay(date, new Date());

            // Check if date is in temp selection or current selection
            const tempSelected =
              (tempStartDate && isSameDay(date, tempStartDate)) ||
              (tempEndDate && isSameDay(date, tempEndDate));
            const currentSelected =
              isSameDay(date, new Date(startDate)) ||
              isSameDay(date, new Date(endDate));
            const isSelected =
              tempSelected || (!tempStartDate && currentSelected);

            // Check if date is in temp range or current range
            const tempInRange =
              tempStartDate &&
              tempEndDate &&
              isWithinInterval(date, {
                start: tempStartDate,
                end: tempEndDate,
              });
            const currentInRange = isWithinInterval(date, {
              start: new Date(startDate),
              end: new Date(endDate),
            });
            const isInRange = tempInRange || (!tempStartDate && currentInRange);

            const isDisabled = isAfter(date, new Date());

            return (
              <button
                key={index}
                onClick={() => !isDisabled && handleDateClick(date)}
                disabled={isDisabled}
                className={cn(
                  "h-8 w-8 rounded-md text-sm transition-colors",
                  !isCurrentMonth && "text-gray-400",
                  isCurrentMonth &&
                    !isDisabled &&
                    "text-gray-900 hover:bg-orange-50",
                  isToday && "font-bold",
                  isInRange && !isSelected && "bg-orange-50",
                  isDisabled && "opacity-50 cursor-not-allowed"
                )}
                style={
                  isSelected
                    ? {
                        backgroundColor: dashboardColors.primary,
                        color: "white",
                      }
                    : {}
                }
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const daysDiff = differenceInDays(new Date(endDate), new Date(startDate)) + 1;

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors w-full sm:w-auto"
          style={{
            backgroundColor: dashboardColors.bgCard,
            border: `1px solid ${dashboardColors.border}`,
            boxShadow: dashboardShadows.card,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = `${dashboardColors.primary}10`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = dashboardColors.bgCard;
          }}
        >
          <Calendar
            className="h-4 w-4 flex-shrink-0"
            style={{ color: dashboardColors.primary }}
          />
          <span className="font-medium text-sm sm:text-base">
            {getCurrentPresetLabel()}
          </span>
          <span className="text-gray-500 hidden sm:inline">:</span>
          <span className="text-gray-700 text-sm sm:text-base">
            <span className="hidden sm:inline">
              {capitalizeMonth(
                format(new Date(startDate), "dd MMM", { locale: fr })
              )}{" "}
              -{" "}
              {capitalizeMonth(
                format(new Date(endDate), "dd MMM yyyy", { locale: fr })
              )}
            </span>
            <span className="sm:hidden">
              {format(new Date(startDate), "dd/MM", { locale: fr })} -{" "}
              {format(new Date(endDate), "dd/MM/yy", { locale: fr })}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 text-gray-500 ml-auto sm:ml-2 flex-shrink-0" />
        </button>

        {onCompareModeChange && (
          <div className="flex items-center gap-2">
            <Switch
              id="compare-mode"
              checked={!!compareMode}
              onChange={onCompareModeChange}
            />
            <Label
              htmlFor="compare-mode"
              className="cursor-pointer text-sm"
              label="Comparer avec la période précédente"
            />
          </div>
        )}
      </div>

      {compareMode && (
        <div className="mt-2 text-sm text-gray-600">
          Comparaison avec:{" "}
          {capitalizeMonth(
            format(subDays(new Date(startDate), daysDiff), "dd MMM", {
              locale: fr,
            })
          )}{" "}
          -{" "}
          {capitalizeMonth(
            format(subDays(new Date(endDate), daysDiff), "dd MMM yyyy", {
              locale: fr,
            })
          )}
        </div>
      )}

      {isOpen && (
        <div className="absolute top-full left-0 right-0 sm:left-0 sm:right-auto mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 w-full sm:w-auto max-w-full sm:max-w-4xl overflow-hidden">
          <div className="flex flex-col lg:flex-row max-h-[80vh] overflow-y-auto">
            {/* Preset Options */}
            <div className="border-b lg:border-b-0 lg:border-r border-gray-200 p-4 w-full lg:w-48">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Périodes prédéfinies
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-1 gap-1">
                {presetRanges.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => handlePresetClick(preset)}
                    className={cn(
                      "w-full text-left px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap",
                      getCurrentPresetLabel() === preset.label
                        ? "text-white"
                        : "hover:bg-orange-50"
                    )}
                    style={
                      getCurrentPresetLabel() === preset.label
                        ? {
                            backgroundColor: dashboardColors.primary,
                          }
                        : {}
                    }
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar Section */}
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Période personnalisée
              </h3>
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={handlePrevious}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-4">
                  <span className="font-medium text-sm">
                    {capitalizeMonth(
                      format(leftMonth, "MMMM yyyy", { locale: fr })
                    )}
                  </span>
                  <span className="text-gray-400">-</span>
                  <span className="font-medium text-sm">
                    {capitalizeMonth(
                      format(rightMonth, "MMMM yyyy", { locale: fr })
                    )}
                  </span>
                </div>
                <button
                  onClick={handleNext}
                  disabled={isRightCalendarCurrentMonth}
                  className={cn(
                    "p-1 rounded",
                    isRightCalendarCurrentMonth
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-100"
                  )}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-col md:flex-col lg:flex-row gap-4">
                {renderCalendarMonth(leftMonth)}
                {renderCalendarMonth(rightMonth)}
              </div>

              {tempStartDate && tempEndDate && (
                <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <span className="text-sm text-gray-600">
                    {capitalizeMonth(
                      format(tempStartDate, "dd MMM yyyy", { locale: fr })
                    )}{" "}
                    -{" "}
                    {capitalizeMonth(
                      format(tempEndDate, "dd MMM yyyy", { locale: fr })
                    )}
                  </span>
                  <button
                    onClick={applyDateRange}
                    className="px-4 py-2 text-white text-sm rounded-md transition-colors w-full sm:w-auto"
                    style={{
                      backgroundColor: dashboardColors.primary,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        dashboardColors.primaryHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        dashboardColors.primary;
                    }}
                  >
                    Appliquer
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
